import { formatAmount } from '../../utils/orderDisplay'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

import { DataTable } from '@/components/ui/data-table'

import { StatusBadge, Notice } from '../products/PageHelpers'
import { Clock, Lock, ArrowRight, Percent, ShieldCheck } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { getBalance } from '../../services/balance'
import { checkPayoutEligibility } from '../../services/payout'
import type { PayoutEligibilityResponse } from '../../services/payout'
import { getMerchant } from '../../services/merchant'
import { getSettlementConfig } from '../../services/config'
import type {
  AcquiringPaymentMethod,
  Balance as BalanceType,
  Merchant,
  MerchantAcquiringRate,
  PayoutCapability,
} from '../../types'

type SettlementRateRow = {
  key: string
  channel: string
  fee: string
  minimum: string
}

const paymentMethodLabels: Record<AcquiringPaymentMethod, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch'n Go eWallet",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  PAYPAY: 'PayPay',
  MERPAY: 'Merpay',
  AUPAY: 'au PAY',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  KAKAO_PAY: 'Kakao Pay',
  NAVER_PAY: 'Naver Pay',
  TOSS_PAY: 'Toss Pay',
  PAYCO: 'PAYCO',
  SAMSUNG_PAY: 'Samsung Pay',
  T_MONEY: 'T-money',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
  USDT_DIRECT: 'USDT Direct',
  COINS_PH: 'Coins.ph',
  GO_PAY: 'GoPay',
  GRAB_PAY: 'GrabPay',
  MAYA: 'Maya',
  MOMO: 'MoMo',
  TRUE_MONEY: 'TrueMoney',
  ZALO_PAY: 'ZaloPay',
  PROMPT_PAY: 'PromptPay',
  VIET_QR: 'VietQR',
  DUITNOW: 'DuitNow',
}

const PAYOUT_CAPABILITY_ROWS: Array<{
  capability: PayoutCapability
  channel: string
}> = [
  { capability: 'OVERSEAS_BUSINESS_ACCOUNT', channel: '海外企業賬戶' },
  {
    capability: 'OVERSEAS_EWALLET_ORDINARY',
    channel: '海外電子錢包（普通提現）',
  },
  {
    capability: 'OVERSEAS_EWALLET_URGENT',
    channel: '海外電子錢包（緊急提現）',
  },
  { capability: 'PERSONAL_EWALLET', channel: '個人電子錢包' },
  { capability: 'PERSONAL_BANK', channel: '個人銀行賬戶' },
]

export default function Balance() {
  const [balance, setBalance] = useState<BalanceType | null>(null)
  const [eligibility, setEligibility] =
    useState<PayoutEligibilityResponse | null>(null)
  const [merchant, setMerchant] = useState<Merchant | null>(null)
  const [settlementDays, setSettlementDays] = useState<number>(1)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const handleCreatePayout = () => {
    navigate('/dashboard/payouts/new')
  }

  useEffect(() => {
    Promise.all([
      getBalance(),
      checkPayoutEligibility(),
      getMerchant(),
      getSettlementConfig(),
    ])
      .then(([balanceRes, eligibilityRes, merchantRes, settlementRes]) => {
        setBalance(balanceRes.data)
        setEligibility(eligibilityRes.data)
        setMerchant(merchantRes.data)
        setSettlementDays(settlementRes.data.settlementDays)
      })
      .finally(() => setLoading(false))
  }, [])

  const formatRate = (rate: string) => {
    return `${(Number(rate) * 100).toFixed(2).replace(/\.?0+$/, '')}%`
  }

  const formatMoney = (amount: string | number) => `$${formatAmount(amount)}`
  const formatUsdFee = (amount: string | number) =>
    `USD ${formatAmount(amount)}`
  const formatFee = (rate: string, fixed?: string | null) => {
    const rateAmount = Number(rate)
    const fixedAmount = fixed == null ? 0 : Number(fixed)
    const parts: string[] = []
    if (rateAmount > 0) parts.push(formatRate(rate))
    if (fixedAmount > 0) parts.push(formatUsdFee(fixedAmount))
    return parts.length > 0 ? parts.join(' + ') : formatMoney(0)
  }
  const formatDays = (days: number) => `${days} 天`

  const acquiringRateColumns = [
    {
      key: 'paymentMethod',
      header: '收單渠道',
      render: (row: MerchantAcquiringRate) => {
        const method = row['paymentMethod']
        return paymentMethodLabels[method] || method
      },
    },
    {
      key: 'feeRate',
      header: '費率/單筆費用 (固定費 USD)',
      className: 'text-right tabular-nums',
      render: (row: MerchantAcquiringRate) => {
        return (
          <StatusBadge color={'blue'}>
            {formatFee(row.feeRate, row.perTransactionFee)}
          </StatusBadge>
        )
      },
    },
    {
      key: 'chargebackRate',
      header: '拒付費率/單筆拒付手續費 (固定費 USD)',
      className: 'text-right tabular-nums',
      render: (row: MerchantAcquiringRate) => {
        return formatFee(row.chargebackRate, row.chargebackFixedFee)
      },
    },
    {
      key: 'rollingReserveRate',
      header: '循環保證金比例/循環保證金天數',
      className: 'text-right tabular-nums',
      render: (row: MerchantAcquiringRate) => {
        return `${formatRate(row.rollingReserveRate)} / ${formatDays(row.rollingReserveDays)}`
      },
    },
    {
      key: 'settlementDays',
      header: '結算週期',
      className: 'text-right tabular-nums',
      render: (row: MerchantAcquiringRate) => {
        const settlementDays = row['settlementDays']
        return `T+${settlementDays}`
      },
    },
  ]

  const settlementRateRows: SettlementRateRow[] = eligibility
    ? PAYOUT_CAPABILITY_ROWS.filter(({ capability }) =>
        eligibility.enabledCapabilities.includes(capability),
      ).map(({ capability, channel }) => ({
        key: capability,
        channel,
        fee: formatFee(
          eligibility.feeConfigs[capability].feeRate,
          eligibility.feeConfigs[capability].fixedFee,
        ),
        minimum: eligibility.minPayoutAmounts[capability],
      }))
    : []

  const settlementRateColumns = [
    {
      key: 'channel',
      header: '結款渠道',
      render: (row: SettlementRateRow) => row['channel'] ?? '-',
    },
    {
      key: 'fee',
      header: '費率',
      className: 'text-right tabular-nums',
      render: (row: SettlementRateRow) => {
        const fee = row['fee']
        return <StatusBadge color={'geekblue'}>{fee}</StatusBadge>
      },
    },
    {
      key: 'minimum',
      header: '最低結款金額',
      className: 'text-right tabular-nums',
      render: (row: SettlementRateRow) => {
        const minimum = row['minimum']
        return `${formatMoney(minimum)} USD`
      },
    },
  ]

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <h2
          style={{ margin: 0 }}
          className="mb-4 text-xl font-semibold tracking-tight"
        >
          餘額概覽
        </h2>
        <Button
          onClick={() => navigate('/dashboard/balance/transactions')}
          variant={'outline'}
          type="button"
        >
          交易明細 <ArrowRight />
        </Button>
      </div>

      {eligibility && !eligibility.eligible && (
        <Notice
          tone={'warning'}
          title={'結款提示'}
          description={eligibility.reasons.join('；')}
          style={{ marginBottom: 24 }}
        />
      )}

      <div className="grid auto-rows-fr gap-6 md:grid-cols-4">
        <div>
          <Card className="h-full">
            <div className="flex flex-1 flex-col gap-4 px-5">
              {loading ? (
                <div role="status">載入中…</div>
              ) : (
                <>
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">
                      {'可用餘額'}
                    </p>
                    <div
                      className="flex items-center gap-2 text-2xl font-semibold"
                      style={{ color: '#52c41a' }}
                    >
                      {'$'}
                      {balance ? formatAmount(balance.available) : 0}{' '}
                      <span className="text-sm">{'USD'}</span>
                    </div>
                  </div>
                  <Button
                    onClick={handleCreatePayout}
                    disabled={!eligibility?.eligible}
                    className="mt-auto self-start"
                    variant={'default'}
                    type="button"
                  >
                    發起結款
                  </Button>
                </>
              )}
            </div>
          </Card>
        </div>
        <div>
          <Card className="h-full">
            <div className="flex flex-1 flex-col gap-4 px-5">
              {loading ? (
                <div role="status">載入中…</div>
              ) : (
                <>
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">
                      {'待結算'}
                    </p>
                    <div
                      className="flex items-center gap-2 text-2xl font-semibold"
                      style={{ color: '#faad14' }}
                    >
                      {<Clock />}
                      {balance ? formatAmount(balance.pending) : 0}{' '}
                      <span className="text-sm">{'USD'}</span>
                    </div>
                  </div>
                  <p className="mt-auto text-xs text-muted-foreground">
                    支付成功後資金將在 T+{settlementDays}{' '}
                    個工作日後結算到可用餘額
                  </p>
                </>
              )}
            </div>
          </Card>
        </div>
        <div>
          <Card className="h-full">
            <div className="flex flex-1 flex-col gap-4 px-5">
              {loading ? (
                <div role="status">載入中…</div>
              ) : (
                <>
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">
                      {'凍結金額'}
                    </p>
                    <div
                      className="flex items-center gap-2 text-2xl font-semibold"
                      style={{ color: '#ff4d4f' }}
                    >
                      {<Lock />}
                      {balance ? formatAmount(balance.frozen) : 0}{' '}
                      <span className="text-sm">{'USD'}</span>
                    </div>
                  </div>
                  <p className="mt-auto text-xs text-muted-foreground">
                    因拒付或風控被凍結的資金
                  </p>
                </>
              )}
            </div>
          </Card>
        </div>
        <div>
          <Card className="h-full">
            <div className="flex flex-1 flex-col gap-4 px-5">
              {loading ? (
                <div role="status">載入中…</div>
              ) : (
                <>
                  <div>
                    <p className="mb-2 text-sm text-muted-foreground">
                      {'循環保證金'}
                    </p>
                    <div
                      className="flex items-center gap-2 text-2xl font-semibold"
                      style={{ color: '#1677ff' }}
                    >
                      {<ShieldCheck />}
                      {balance ? formatAmount(balance.rollingReserve) : 0}{' '}
                      <span className="text-sm">{'USD'}</span>
                    </div>
                  </div>
                  <p className="mt-auto text-xs text-muted-foreground">
                    依收單渠道規則暫扣，達到釋放時間後轉入可用餘額
                  </p>
                </>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Card style={{ marginTop: 24 }}>
        <div className="space-y-4 px-5">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b pb-4">
            <h3 className="font-semibold">
              {
                <span>
                  <Percent style={{ marginRight: 8, color: '#1677ff' }} />
                  費率說明
                </span>
              }
            </h3>
          </div>
          {loading ? (
            <div role="status">載入中…</div>
          ) : (
            <>
              {merchant && (
                <>
                  <h2
                    style={{ marginTop: 0 }}
                    className="mb-4 text-xl font-semibold tracking-tight"
                  >
                    收單費率
                  </h2>
                  <DataTable
                    rowKey={(row) => row.paymentMethod}
                    columns={acquiringRateColumns}
                    data={merchant.acquiringRates}
                    emptyText={{ emptyText: '暫未配置收單渠道' }.emptyText}
                  />

                  <h2
                    style={{ marginTop: 24 }}
                    className="mb-4 text-xl font-semibold tracking-tight"
                  >
                    結款費率
                  </h2>
                  <DataTable
                    columns={settlementRateColumns}
                    data={settlementRateRows}
                    rowKey="key"
                    emptyText={{ emptyText: '暫無結款費率資料' }.emptyText}
                  />

                  <p
                    style={{
                      marginTop: 12,
                      marginBottom: 0,
                      color: '#999',
                      fontSize: 12,
                    }}
                  >
                    單筆固定手續費和單筆拒付固定手續費以 USD
                    設定，不跟隨訂單貨幣；如需調整收單渠道或費率，請聯絡管理員。
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
